import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import type { BackendModule, ResourceLanguage } from 'i18next';
import { initReactI18next } from 'react-i18next';

import { LINGUE, linguaDaTelegram, linguaSalvata, lingueDelBrowser, linguaSupportata } from '@/lib/lingua';

import enChallenges from '@/locales/en/challenges.json';
import enGeneral from '@/locales/en/general.json';
import enHub from '@/locales/en/hub.json';
import enNews from '@/locales/en/news.json';
import enRiot from '@/locales/en/riot.json';
import enTeams from '@/locales/en/teams.json';
import enTournaments from '@/locales/en/tournaments.json';
import enCr from '@/locales/en/cr.json';
import enDuels from '@/locales/en/duels.json';
import enChess from '@/locales/en/chess.json';
import enCoc from '@/locales/en/coc.json';
import enBackgammon from '@/locales/en/backgammon.json';
import enChinesePoker from '@/locales/en/chinesepoker.json';
import enActiveMatch from '@/locales/en/activeMatch.json';
import enAvatarAssistant from '@/locales/en/avatarAssistant.json';
import enAvatars from '@/locales/en/avatars.json';
import enLeaderboard from '@/locales/en/leaderboard.json';
import enOnboarding from '@/locales/en/onboarding.json';
import enMissions from '@/locales/en/missions.json';
import enLimulo from '@/locales/en/limulo.json';
import enChessMatch from '@/locales/en/chessMatch.json';
import enChessBot from '@/locales/en/chessBot.json';
import enBridge from '@/locales/en/bridge.json';
import enBurraco from '@/locales/en/burraco.json';
import enQ2048 from '@/locales/en/q2048.json';
import enBriscola from '@/locales/en/briscola.json';
import enGinrummy from '@/locales/en/ginrummy.json';
import enScala40 from '@/locales/en/scala40.json';
import enSettemezzo from '@/locales/en/settemezzo.json';
import enTressette from '@/locales/en/tressette.json';
import enPilotta from '@/locales/en/pilotta.json';
import enMpirimpa from '@/locales/en/mpirimpa.json';
import enScopa from '@/locales/en/scopa.json';
import enSolitario from '@/locales/en/solitario.json';
// ADR-0115: namespace GENERICO degli avvisi di atterraggio. Non e' `backgammon`
// di proposito — «si scrive UNA volta» vale anche per le chiavi i18n, e una
// frase sull'atterraggio ospitata nel namespace di un gioco e' gia' una
// schermata per tipo, scritta nei file di traduzione invece che nel codice.
import enLanding from '@/locales/en/landing.json';

import itChallenges from '@/locales/it/challenges.json';
import itGeneral from '@/locales/it/general.json';
import itHub from '@/locales/it/hub.json';
import itNews from '@/locales/it/news.json';
import itRiot from '@/locales/it/riot.json';
import itTeams from '@/locales/it/teams.json';
import itTournaments from '@/locales/it/tournaments.json';
import itCr from '@/locales/it/cr.json';
import itDuels from '@/locales/it/duels.json';
import itChess from '@/locales/it/chess.json';
import itCoc from '@/locales/it/coc.json';
import itBackgammon from '@/locales/it/backgammon.json';
import itChinesePoker from '@/locales/it/chinesepoker.json';
import itActiveMatch from '@/locales/it/activeMatch.json';
import itAvatarAssistant from '@/locales/it/avatarAssistant.json';
import itAvatars from '@/locales/it/avatars.json';
import itLeaderboard from '@/locales/it/leaderboard.json';
import itOnboarding from '@/locales/it/onboarding.json';
import itMissions from '@/locales/it/missions.json';
import itLimulo from '@/locales/it/limulo.json';
import itChessMatch from '@/locales/it/chessMatch.json';
import itChessBot from '@/locales/it/chessBot.json';
import itBridge from '@/locales/it/bridge.json';
import itBurraco from '@/locales/it/burraco.json';
import itQ2048 from '@/locales/it/q2048.json';
import itBriscola from '@/locales/it/briscola.json';
import itGinrummy from '@/locales/it/ginrummy.json';
import itScala40 from '@/locales/it/scala40.json';
import itSettemezzo from '@/locales/it/settemezzo.json';
import itTressette from '@/locales/it/tressette.json';
import itPilotta from '@/locales/it/pilotta.json';
import itMpirimpa from '@/locales/it/mpirimpa.json';
import itScopa from '@/locales/it/scopa.json';
import itSolitario from '@/locales/it/solitario.json';
import itLanding from '@/locales/it/landing.json';

/**
 * i18n init. Contratto di fronte `fronti/tma/lingue.md` (KB, I-283).
 *
 * CATALOGHI: `en` (il ripiego, sempre caricato) e `it` (la lingua di quasi tutti gli utenti)
 * stanno nel bundle d'ingresso, com'erano. OGNI ALTRA LINGUA è UN chunk per lingua,
 * caricato a richiesta dal backend qui sotto: il bundle d'ingresso non cresce con le lingue.
 *
 * QUALI LINGUE e IN CHE ORDINE si sceglie: `lib/lingua.ts`. In breve: scelta salvata nelle
 * Preferenze → `?lang=` → `language_code` di Telegram → browser → `en`. Il detector non
 * salva niente (`caches: []`): si salva solo una scelta esplicita (`salvaLingua`).
 *
 * `<html lang>` segue la lingua: senza `lang="el"` le maiuscole CSS (`uppercase`, 334 usi)
 * lasciano i toni sul greco (misurato su Chromium 151 e WebKit 26.5, 2026-10-01).
 */
const rilevatore = new LanguageDetector();
rilevatore.addDetector({ name: 'qlashSalvata', lookup: () => linguaSalvata() });
rilevatore.addDetector({ name: 'telegram', lookup: () => linguaSupportata(linguaDaTelegram()) });
rilevatore.addDetector({
  name: 'browser',
  lookup: () => {
    const lingue = lingueDelBrowser();
    return lingue.length > 0 ? lingue : undefined;
  },
});

/**
 * Le lingue fuori dal bundle d'ingresso: UN chunk per lingua, caricato quando serve.
 * Ogni lingua ha in `src/locales/<lingua>/index.ts` lo stesso modulo di una riga, che mette
 * insieme i suoi file. Un chunk per FILE costava una voce per namespace nella mappa qui sotto,
 * cioè ~1,5 KB del bundle d'ingresso per ogni lingua [M 2026-10-01: 986.359 → 989.405 B con
 * due lingue in più]: il contrario di «il bundle non cresce con le lingue».
 */
const lingueAltre = import.meta.glob<{ default: Record<string, ResourceLanguage> }>([
  '../locales/*/index.ts',
  '!../locales/en/index.ts',
  '!../locales/it/index.ts',
]);
const lingueInArrivo = new Map<string, Promise<Record<string, ResourceLanguage>>>();

const backendPigro: BackendModule = {
  type: 'backend',
  init() {},
  read(lingua, namespace, fatto) {
    const carica = lingueAltre[`../locales/${lingua}/index.ts`];
    if (!carica) {
      fatto(null, {});
      return;
    }
    let catalogo = lingueInArrivo.get(lingua);
    if (!catalogo) {
      catalogo = carica().then((modulo) => modulo.default);
      lingueInArrivo.set(lingua, catalogo);
    }
    catalogo.then(
      (namespaces) => fatto(null, namespaces[namespace] ?? {}),
      (errore: unknown) => {
        lingueInArrivo.delete(lingua);
        fatto(errore instanceof Error ? errore.message : String(errore), false);
      },
    );
  },
};

function allineaHtmlLang(lingua: string | undefined): void {
  if (!lingua || typeof document === 'undefined') return;
  document.documentElement.lang = lingua;
}

i18n.on('languageChanged', () => allineaHtmlLang(i18n.resolvedLanguage ?? i18n.language));

void i18n
  .use(rilevatore)
  .use(backendPigro)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    // Niente `nonExplicitSupportedLngs` / `load: 'languageOnly'`: con quelli `i18n.language`
    // diventa `en-US`, `es-419` invece di `en`, `es`, e chi confronta `i18n.language` col codice
    // base smette di trovarlo. Senza, i18next riduce da sé `es-419` a `es` (misurato nel test).
    supportedLngs: LINGUE,
    partialBundledLanguages: true,
    ns: ['general', 'tournaments', 'teams', 'news', 'hub', 'challenges', 'riot', 'cr', 'duels', 'chess', 'chessBot', 'coc', 'activeMatch', 'backgammon', 'chinesepoker', 'avatarAssistant', 'avatars', 'leaderboard', 'onboarding', 'missions', 'limulo', 'landing', 'briscola', 'scopa', 'ginrummy', 'scala40', 'settemezzo', 'tressette', 'pilotta', 'mpirimpa', 'solitario', 'q2048', 'burraco', 'chessMatch', 'bridge'],
    defaultNS: 'general',
    resources: {
      en: {
        general: enGeneral,
        tournaments: enTournaments,
        teams: enTeams,
        news: enNews,
        hub: enHub,
        challenges: enChallenges,
        riot: enRiot,
        cr: enCr,
        duels: enDuels,
        chess: enChess,
        coc: enCoc,
        activeMatch: enActiveMatch,
        backgammon: enBackgammon,
        chinesepoker: enChinesePoker,
        avatarAssistant: enAvatarAssistant,
        avatars: enAvatars,
        leaderboard: enLeaderboard,
        onboarding: enOnboarding,
        missions: enMissions,
        limulo: enLimulo,
        landing: enLanding,
        chessMatch: enChessMatch,
        chessBot: enChessBot,
        bridge: enBridge,
        burraco: enBurraco,
        briscola: enBriscola,
        scopa: enScopa,
        ginrummy: enGinrummy,
        scala40: enScala40,
        settemezzo: enSettemezzo,
        tressette: enTressette,
        pilotta: enPilotta,
        mpirimpa: enMpirimpa,
        solitario: enSolitario,
        q2048: enQ2048,
      },
      it: {
        general: itGeneral,
        tournaments: itTournaments,
        teams: itTeams,
        news: itNews,
        hub: itHub,
        challenges: itChallenges,
        riot: itRiot,
        cr: itCr,
        duels: itDuels,
        chess: itChess,
        coc: itCoc,
        activeMatch: itActiveMatch,
        backgammon: itBackgammon,
        chinesepoker: itChinesePoker,
        avatarAssistant: itAvatarAssistant,
        avatars: itAvatars,
        leaderboard: itLeaderboard,
        onboarding: itOnboarding,
        missions: itMissions,
        limulo: itLimulo,
        landing: itLanding,
        chessMatch: itChessMatch,
        chessBot: itChessBot,
        bridge: itBridge,
        burraco: itBurraco,
        briscola: itBriscola,
        scopa: itScopa,
        ginrummy: itGinrummy,
        scala40: itScala40,
        settemezzo: itSettemezzo,
        tressette: itTressette,
        pilotta: itPilotta,
        mpirimpa: itMpirimpa,
        solitario: itSolitario,
        q2048: itQ2048,
      },
    },
    interpolation: { escapeValue: false },
    detection: {
      order: ['qlashSalvata', 'querystring', 'telegram', 'browser'],
      lookupQuerystring: 'lang',
      caches: [],
    },
  })
  .then(() => allineaHtmlLang(i18n.resolvedLanguage ?? i18n.language));

export { i18n };
